// Shared wallet-proof signing (mirrors WalletPage): connect a browser wallet by family and return
// the address + a sign(message) fn. EVM uses EIP-191 personal_sign; Solana uses Ed25519 signMessage.
// Pass { via: "walletconnect" } to connect an EVM wallet (e.g. Qastle on KROWN) over WalletConnect v2.
import { connectViaWalletConnect } from "@/lib/walletConnect";

function bytesToBase64(bytes) {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

// Returns { address, family, sign(message) → Promise<signature> }.
// Throws a friendly Error if the wallet isn't available or the family isn't supported on web.
export async function connectWallet(family, opts = {}) {
  if (opts.via === "walletconnect") {
    return connectViaWalletConnect();
  }
  if (family === "sol") {
    const sol = window.solana;
    if (!sol || !sol.signMessage) throw new Error("No Solana wallet detected. Install Phantom, or verify from the BLOQ Talk app.");
    const resp = await sol.connect();
    const address = (resp?.publicKey || sol.publicKey)?.toString();
    if (!address) throw new Error("No account selected");
    return {
      address,
      family: "sol",
      sign: async (message) => bytesToBase64((await sol.signMessage(new TextEncoder().encode(message), "utf8")).signature),
    };
  }
  if (family === "btc") {
    throw new Error("Bitcoin wallets are only supported in the BLOQ Talk app for now.");
  }
  // default: EVM (MetaMask / Rabby / Coinbase)
  const eth = window.ethereum;
  if (!eth) throw new Error("No EVM wallet detected. Install MetaMask, or verify from the BLOQ Talk app.");
  const accounts = await eth.request({ method: "eth_requestAccounts" });
  const address = accounts?.[0];
  if (!address) throw new Error("No account selected");
  return {
    address,
    family: "evm",
    sign: async (message) => eth.request({ method: "personal_sign", params: [message, address] }),
  };
}

export function isUserRejection(e) {
  return e?.code === 4001 || /reject|cancel|denied/i.test(e?.message || "");
}
